import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './FAQ.css';

const faqs = [
  {
    category: "General Information & Eligibility",
    questions: [
      {
        q: "What is Dynamic Exams?",
        a: "Dynamic Exams is an advanced, digital-first Universal Exam Management System designed to host secure, scalable competitive exams and Olympiads. We leverage a modern multi-tenant architecture to streamline everything from school registration to deep statistical analytics, modernizing the entire Olympiad experience."
      },
      {
        q: "Who is eligible to participate in Dynamic Exams Olympiads?",
        a: "Students from primary through senior secondary classes are eligible, depending on the specific Olympiad. Unlike traditional paper-based models, our online infrastructure enables students to participate in concurrent Olympiads from multiple disciplines seamlessly."
      },
      {
        q: "How does Dynamic Exams differ from other Olympiad organizations?",
        a: "Many traditional Olympiad foundations rely heavily on offline, paper-based exams and manual administrative processes. Dynamic Exams operates on a robust mobile-based online platform that supports concurrent exams, real-time candidate telemetry tracking to prevent malpractice, and instant statistical item response analytics. This eliminates administrative bottlenecks and provides actionable insights rather than just raw scores."
      }
    ]
  },
  {
    category: "Registration & Administration",
    questions: [
      {
        q: "Can individual students apply directly?",
        a: "Yes, they can. Furthermore, to ensure organized batch scheduling and accurate performance mapping, registrations are typically routed through participating schools."
      },
      {
        q: "Is there an administrative burden on schools for conducting these exams?",
        a: "No. Our platform is designed to eliminate paperwork. From automated candidate attendance rosters to the generation of unique login codes, the entire workflow is digitized. Schools focus on encouraging participation, while our system handles the logistics."
      }
    ]
  },
  {
    category: "Exam Platform & Technical Details",
    questions: [
      {
        q: "What are the technical requirements for a student taking the exam?",
        a: "Students simply need a stable internet connection and a modern web browser on their mobile phones. The frontend interface is highly optimized and user-friendly."
      },
      {
        q: "How do you ensure the integrity and fairness of an online Olympiad?",
        a: "Exam integrity is our top priority. The platform utilizes advanced candidate telemetry tracking during the examination. This allows us to monitor test-taking behaviour, track anomalies, and maintain a secure environment without relying on traditional offline invigilation."
      },
      {
        q: "Can the platform handle multiple schools and exams at once?",
        a: "Yes. The platform runs on a scalable, multi-tenant architecture capable of supporting thousands of concurrent users and multiple distinct Olympiads simultaneously without performance degradation."
      }
    ]
  },
  {
    category: "Results, Analytics & Reporting",
    questions: [
      {
        q: "How and when are the Olympiad results declared?",
        a: "Because the exams are digital, result processing begins the moment the exam concludes. Results are published significantly faster than traditional offline exams, which require manual OMR sheet transportation and scanning. Result of an individual candidate is displayed immediately after s/he makes the submission, ensuring complete transparency and fairness."
      },
      {
        q: "What kind of information is included in the results?",
        a: "We go far beyond standard grading. Our system utilizes statistical item response analytics to provide a deep breakdown of candidate performance. This helps students and schools identify specific strengths and areas requiring improvement based on question difficulty and topic parameters. These are depicted in the Detailed Exam Report of the student."
      },
      {
        q: "How are reports delivered to schools?",
        a: "Students will be mailed their Certificates of Participation and Detailed Exam Reports."
      }
    ]
  },
  {
    category: "Syllabus and Technical Issues",
    questions: [
      {
        q: "What syllabus do the Olympiads follow?",
        a: "The curriculum is strictly aligned with major educational boards (CBSE, ICSE, and State Boards). The questions are designed to evaluate applied knowledge, analytical thinking, and statistical problem-solving rather than rote memorization."
      },
      {
        q: "What happens if a student's internet connection drops during the exam?",
        a: "Our platform is engineered for high-concurrency resilience. If a connection fails, the student's progress is instantly saved on our backend. Upon reconnecting, they can resume the exam exactly where they left off without losing any data or allocated time."
      },
      {
        q: "How do you prevent cheating without physical invigilators?",
        a: "Dynamic Exams replaces manual offline invigilation with automated digital proctoring. The platform monitors candidate telemetry—tracking browser behaviour, tab-switching, and irregular answering patterns—to detect and flag malpractice in real time."
      }
    ]
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(group => group.questions.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    })))
  };

  return (
    <div className="faq-page">
      <Helmet>
        <title>Frequently Asked Questions | Dynamic Exams</title>
        <meta name="description" content="Find answers to common questions about our Olympiads, registration, eligibility, and the digital examination platform." />
        <meta property="og:title" content="FAQ | Dynamic Exams" />
        <meta property="og:description" content="Find answers to common questions about our Olympiads and platform." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <section className="page-header faq-header">
        <div className="container">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about our Olympiads, Registration, and Examinations.</p>
        </div>
      </section>

      <section className="faq-content">
        <div className="container">
          <div className="faq-wrapper">
            {faqs.map((group, groupIdx) => (
              <div key={groupIdx} className="faq-category mb-5">
                <h2 className="category-title glass-panel-dark" style={{padding: '1rem', borderRadius: '8px', marginBottom: '1rem', backgroundColor: 'rgba(5, 11, 20, 0.7)', border: '1px solid rgba(255, 215, 0, 0.2)'}}>
                  <HelpCircle size={24} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '0.5rem', color: 'var(--secondary-gold)' }} />
                  {group.category}
                </h2>
                <div className="accordion">
                  {group.questions.map((faq, idx) => {
                    const id = `${groupIdx}-${idx}`;
                    return (
                      <div key={id} className={`accordion-item ${openIndex === id ? 'open' : ''}`}>
                        <button className="accordion-header" onClick={() => toggleAccordion(id)}>
                          <span>{faq.q}</span>
                          <ChevronDown className="accordion-icon" />
                        </button>
                        <div className="accordion-body">
                          <div className="accordion-content">
                            {faq.a}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="legal-documents-section glass-panel mt-5" style={{ padding: '2rem', textAlign: 'center', marginTop: '3rem' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Legal Documents</h3>
            <p style={{ marginBottom: '1.5rem', color: '#a0a0a0' }}>Please review our policies governing the use of the Dynamic Professional Skills platform.</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
              <Link to="/privacy-policy" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center' }}>
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center' }}>
                Terms & Conditions
              </Link>
              <Link to="/refund-and-cancellation" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center' }}>
                Refund & Cancellation Policy
              </Link>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

